import { api } from '@/lib/api/client'

/**
 * Cursor pagination, followed to the end.
 *
 * Eleven controllers cursor-paginate at fifty. The client used to read `data`
 * and drop `meta.next_cursor` on the floor, so every list in the product
 * silently stopped at fifty records — the portfolio counted seventy projects
 * from one endpoint and listed fifty from another, with nothing on screen to
 * say so. A list that quietly omits records is worse than one that fails.
 *
 * The page size is the server's business; this just keeps asking until it says
 * there is no more. The cap is a runaway guard, not a limit anyone should
 * reach: at fifty a page it is 5,000 records, far past any list this product
 * puts on one screen.
 */
const MAX_PAGES = 100

interface Envelope<T> {
  data: T[]
  meta?: { next_cursor?: string | null }
}

export async function fetchAll<T>(
  path: string,
  params: Record<string, string | number | boolean> = {},
): Promise<T[]> {
  const items: T[] = []
  let cursor: string | null = null

  for (let page = 0; page < MAX_PAGES; page++) {
    const { data }: { data: Envelope<T> } = await api.get<Envelope<T>>(path, {
      params: cursor === null ? params : { ...params, cursor },
    })

    items.push(...data.data)

    cursor = data.meta?.next_cursor ?? null

    if (cursor === null) break
  }

  return items
}
